import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useNavigate } from "react-router-dom";
import React from "react";
import { useCelebrations } from "@/hooks/useCelebrations";

export function useNotificacaoFatura() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { notification } = useCelebrations();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('fatura-recebida-notification')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'solicitacao_fatura',
          filter: `solicitante_id=eq.${user.id}`
        },
        async (payload) => {
          const newRecord = payload.new as any;
          const oldRecord = payload.old as any;
          
          // Só notifica quando muda para FATURA_RECEBIDA
          if (newRecord.status === 'FATURA_RECEBIDA' && oldRecord.status !== 'FATURA_RECEBIDA') {
            // Toca som de notificação
            notification();
            
            // Mostra toast
            // (Douglas 16/07) Compacto, cor do sistema, IDENTIFICAVEL (nome + mes +
            // valor + linhas) e com botao que LEVA pra pagina de faturas.
            let quem = '';
            let detalhe = '';
            try {
              // (Douglas 16/07 v3) Fonte = a PROPRIA resposta do robo (resposta_n8n), que ja
              // vem no payload: mes (fileName _M_AAAA.pdf), QUANTIDADE de linhas (dados_fev)
              // e valor (soma de valor_utilizado). fatura_upload nao serve aqui: na hora do
              // aviso o OCR ainda nao rodou (valor null, linhas 0).
              const { data: leadRow } = await supabase
                .from('leads_carteira').select('razao_social')
                .eq('cnpj', String(newRecord.cnpj || '')).limit(1).maybeSingle();
              // Razao vem com o CNPJ prefixado (digitos/pontos/traco) — limpa tudo.
              quem = String((leadRow as any)?.razao_social || '').replace(/^[\d./-]+\s*/, '');
              const resp: any = typeof newRecord.resposta_n8n === 'string'
                ? JSON.parse(newRecord.resposta_n8n || '{}')
                : (newRecord.resposta_n8n || {});
              const dados: any[] = Array.isArray(resp?.dados_fev) ? resp.dados_fev : [];
              const qtd = dados.length;
              const soma = dados.reduce((acc, d) => {
                const v = parseFloat(String(d?.valor_utilizado ?? '').replace(/[^0-9,.-]/g, '').replace(',', '.'));
                return acc + (Number.isFinite(v) ? v : 0);
              }, 0);
              const MESES = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
              const mm = String(resp?.fileName || '').match(/_(\d{1,2})_(\d{4})\.pdf$/i);
              const mes = mm ? `${MESES[Math.max(0, Math.min(11, parseInt(mm[1], 10) - 1))]}/${mm[2]}` : null;
              const valor = soma > 0 ? soma.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : null;
              const linhas = qtd > 0 ? `${qtd} linha${qtd > 1 ? 's' : ''}` : null;
              detalhe = [mes, valor, linhas].filter(Boolean).join(' · ');
            } catch { /* enriquecimento best-effort */ }
            // ⛔ 1ª FATURA DO CICLO (Douglas 24/09/2026): outra cor para saber de
            // longe que é a conta do ciclo da Qualidade — vinho cheio, como o topo
            // das triagens — e numa linha só, sem crescer. O pedido do ciclo é o
            // que carrega ciclo_id; a conta da carteira segue no cartão de sempre.
            const doCiclo = !!newRecord.ciclo_id;
            const cnpjDigitos = String(newRecord.cnpj || '').replace(/\D/g, '');
            toast({
              title: React.createElement('div', { className: `text-[13px] font-semibold leading-tight truncate ${doCiclo ? 'text-white' : ''}` },
                doCiclo ? `1ª fatura chegou — ${quem || 'Cliente'}` : `Fatura recebida — ${quem || 'Cliente'}`) as any,
              description: React.createElement('div', { className: `text-xs leading-tight truncate ${doCiclo ? 'text-white/65' : 'text-muted-foreground'}` },
                `${doCiclo ? 'Ciclo da Qualidade · ' : ''}CNPJ ${formatCNPJ(newRecord.cnpj)}${detalhe ? ' · ' + detalhe : ''}`) as any,
              duration: 8000,
              className: doCiclo
                ? "w-full max-w-[420px] rounded-xl border border-white/[0.07] bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] py-2.5 pl-3.5 pr-2 shadow-md gap-2 overflow-hidden [&>div]:min-w-0 [&_button[toast-close]]:text-white/70"
                : "w-full max-w-[380px] rounded-xl border border-border border-l-4 border-l-brand-borgonha bg-card py-2.5 pl-3.5 pr-2 shadow-md gap-2 overflow-hidden [&>div]:min-w-0",
              action: React.createElement(ToastAction as any, {
                altText: doCiclo ? 'Abrir na triagem' : 'Abrir faturas',
                className: doCiclo
                  ? 'bg-white text-primary hover:bg-white/90 border-0 rounded-md h-7 px-3 text-xs font-semibold shrink-0 whitespace-nowrap'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90 border-0 rounded-md h-7 px-3 text-xs font-medium shrink-0',
                onClick: () => {
                  // Navegacao REAL do navegador (16/07): o navigate SPA dentro do
                  // ToastAction nao disparava em alguns contextos — assign sempre vai.
                  const dest = doCiclo
                    ? `/triagens?triagem=primeira_fatura&busca=${cnpjDigitos}`
                    : '/comercial?tab=faturas';
                  try { window.location.assign(dest); } catch { navigate(dest); }
                },
              }, doCiclo ? 'Abrir na triagem' : 'Abrir'),
            });

            // Abre download automaticamente se tiver URL
            if (newRecord.arquivo_url) {
              window.open(newRecord.arquivo_url, '_blank');
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, toast, notification, navigate]);
}

function formatCNPJ(cnpj: string): string {
  if (!cnpj) return '';
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,12)}-${digits.slice(12)}`;
}
